import React, { useEffect } from 'react';
import { PillTabs } from '../../components/dashboard/pill-tabs.component';
import Sidebar from '../../components/Sidebar';
import { Header } from '../../components/header';
import { DashboardButton } from '../../components/dashboard/dashboard-button.component';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { InvoiceTableDetailed } from '../../components/dashboard/invoice-table-detailed.component';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useCallback, useMemo } from 'react';
import { useInvoiceSummary } from '../../hooks/useInvoiceSummary';
import { KpiCardsSkeleton, InvoiceTableSkeleton } from '../../components/dashboard/manage-invoice-skeleton.component';
import ManageInvoiceTour from '../../components/dashboard/manage-invoice-tour.component';
import { FirstTimeTourPrompt } from '../../components/tour/FirstTimeTourPrompt';
import { getUserOnboarding } from '../../../redux/auth/authSlice';
import { updateUserOnboarding } from '../../features/onboarding/onboarding.service';
import { AppDispatch } from '../../store';
import './../../scss/components/manage-invoice.style.scss';

/* Safely parse the `onBoardingSteps` field from the /useronboarding
   GET response — it arrives as a JSON string, not a nested object.
   Returns an empty object when the field is missing / malformed so
   callers can read flags off it without null checks. */
const parseOnboardingSteps = (raw: unknown): Record<string, unknown> => {
    if (typeof raw !== 'string' || !raw) return {};
    try {
        const parsed = JSON.parse(raw);
        return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch { return {}; }
};

// Lazy load credit invoice grid (module scope so it has a stable identity)
const CreditInvoiceTableDetailed = React.lazy(() => import('../../components/dashboard/credit-invoice-table-detailed.component'));
// Lazy load shared invoice grid (module scope so it has a stable identity)
const SharedInvoiceTableDetailed = React.lazy(() => import('../../components/dashboard/shared-invoice-table-detailed.component'));

/* Tiny sparkles icon for the hero badge. Inline so the page has no
   new icon-library dependency. */
const HeroSparkles = () => (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0z" />
        <path d="M20 3v4" /><path d="M22 5h-4" /><path d="M4 17v2" /><path d="M5 18H3" />
    </svg>
);

/* Render the title with the LAST word styled as a violet gradient brand
   accent — works for any locale. Single-word titles are fully styled. */
const renderHeroTitle = (title: string) => {
    const trimmed = (title || '').trim();
    if (!trimmed) return null;
    const idx = trimmed.lastIndexOf(' ');
    if (idx === -1) {
        return <span className="mi-hero__brand">{trimmed}</span>;
    }
    const prefix = trimmed.slice(0, idx);
    const last = trimmed.slice(idx + 1);
    return (
        <>
            {prefix}{' '}
            <span className="mi-hero__brand">{last}</span>
        </>
    );
};

const PlusIcon: React.FC = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={15}
        height={15}
        viewBox="0 0 15 15"
        fill="none"
    >
        <path
            d="M7.41667 4.75V7.41667M7.41667 7.41667V10.0833M7.41667 7.41667H10.0833M7.41667 7.41667H4.75M14.0833 7.41667C14.0833 11.0986 11.0986 14.0833 7.41667 14.0833C3.73477 14.0833 0.75 11.0986 0.75 7.41667C0.75 3.73477 3.73477 0.75 7.41667 0.75C11.0986 0.75 14.0833 3.73477 14.0833 7.41667Z"
            stroke="#544196"
            strokeWidth="1.5"
            strokeLinecap="round"
        />
    </svg>
)

const TrendingIcon: React.FC = () => (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 17L9 11L13 15L21 7M21 7H15M21 7V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const CheckCircleIcon: React.FC = () => (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M22 4 12 14.01l-3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)


function ManageInvoicePage() {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate()
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    let lang = searchParams.get("lang");
    function handleInvoice() {
        lang?.includes('en-us') ? navigate('/v2/create-invoice?lang=en-us') : navigate('/v2/create-invoice?lang=sv-se')
    }

    const {
        totalAmount,
        paidAmount,
        paidPercentage,
        fetchSummary,
        loading: summaryLoading,
    } = useInvoiceSummary();

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary]);

    // Guaranteed shimmer skeleton on first mount so chrome has time to settle.
    const [showInitialSkeleton, setShowInitialSkeleton] = useState<boolean>(true);
    useEffect(() => {
        const timer = setTimeout(() => setShowInitialSkeleton(false), 1000);
        return () => clearTimeout(timer);
    }, []);

    // Tour state — opened from the Start tour button inside the hero header.
    const [tourOpen, setTourOpen] = useState<boolean>(false);

    /* Redux plumbing for the server-driven onboarding flag. Replaces
       the previous localStorage-only approach so the "first-time"
       decision follows the user across devices and browsers instead
       of resetting on cache-clear. */
    const dispatch = useDispatch<AppDispatch>();

    /* Per-user identity for the tour-prompt storage key. Without this
       the prompt would never show for a newly-registered user who
       opens the app in a browser where a PREVIOUS user has already
       dismissed it — the prompt's internal localStorage flag is
       shared across accounts otherwise. Scoping the storage key by
       userId guarantees each account gets its own "first time"
       experience. Also gates the JSX mount below — we only render
       the prompt once the auth store has resolved a userId, so the
       PUT that fires on click has an authenticated store slice to
       write against. */
    const userId = useSelector((state: any) => state?.auth?.user?.userId);
    /* First name for the tour's finish-step "Congratulations {name}!" cheer. */
    const userName = useSelector((state: any) => state?.auth?.user?.firstName || state?.auth?.user?.name || '');

    /* Server-driven "should the Welcome to Manage Invoice modal show?"
       flag. Sourced from /useronboarding GET's
       `onBoardingSteps.IsManageInvoiceTourSkip` (arrives as a JSON
       string in the payload). The modal renders ONLY while the flag
       is explicitly false; once the user picks either Skip for Now
       or Start Tour, we PUT the flag to true and refresh the GET,
       so the modal stays hidden on every future visit until the flag
       is reset server-side. Contract:
         • null  — GET not yet resolved (don't show)
         • false — first-time user; SHOW the modal
         • true  — user has already dismissed; NEVER show */
    const userOnboardingData = useSelector((state: any) => state?.auth?.userOnboardingData);
    const isManageInvoiceTourSkip: boolean | null = useMemo(() => {
        if (!userOnboardingData) return null;
        const steps = parseOnboardingSteps(userOnboardingData?.onBoardingSteps);
        const flag = steps?.IsManageInvoiceTourSkip;
        return typeof flag === 'boolean' ? flag : null;
    }, [userOnboardingData]);

    /* Fetch the onboarding flags on every mount so the modal decision
       is always driven by the latest server state (not a stale Redux
       value from another page). Dispatched once on mount only. */
    useEffect(() => {
        dispatch(getUserOnboarding());
    }, [dispatch]);

    /* Pulse state for the Start Tour CTA. Flipped true when the
       first-time prompt transitions to the skip-confirmation view,
       flipped back off when the user dismisses that view — so the
       hero button visibly draws attention for the whole time the
       user is reading the "No problem!" copy. Replaces the previous
       fixed-timer skip-hint toast; the prompt component now owns the
       skip-confirmation UI itself. */
    const [pulseStartTour, setPulseStartTour] = useState<boolean>(false);

    const collectionProgress = Math.min(Math.max(paidPercentage, 0), 100);
    const currency = t('kr');

    // KPI amounts: always 2 decimal places
    const fmtAmount = (n: number) =>
        new Intl.NumberFormat(lang === 'sv-se' ? 'sv-SE' : 'en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(Number(n) || 0);

    // Tab logic — seed from the URL so refresh / back-button / shared links
    // restore the tab the user was on. Only 'manage', 'credit' and 'shared'
    // are accepted; anything else falls back to 'manage'.
    const tabFromUrl = searchParams.get('tab');
    const initialTab = (tabFromUrl === 'credit' || tabFromUrl === 'shared') ? tabFromUrl : 'manage';
    const [activeTab, setActiveTab] = useState<string>(initialTab);
    const tabs = [
        { key: 'manage', label: t('ACTIVE_INVOICES', { defaultValue: 'Active Invoices' }) },
        { key: 'credit', label: t('CREDIT_INVOICES') },
        { key: 'shared', label: t('SHARED_INVOICES', { defaultValue: 'Shared Invoices' }) },
    ];

    // Mirror tab changes into the URL (?tab=manage|credit|shared) without
    // reloading the page. Uses replace:true so the back button still jumps
    // the user out of this page instead of cycling through every tab toggle
    // they made.
    const handleTabChange = useCallback((next: string) => {
        setActiveTab(next);
        const params = new URLSearchParams(searchParams);
        if (next === 'manage') {
            params.delete('tab');
        } else {
            params.set('tab', next);
        }
        setSearchParams(params, { replace: true });
    }, [searchParams, setSearchParams]);

    // Keyboard shortcut: press "/" anywhere on the page (when no input is
    // already focused) to jump straight into the table's search box. The
    // search field is owned by the lazy-loaded table component, so we find
    // it by class — pragmatic and survives table internals changing.
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key !== '/') return;
            const target = e.target as HTMLElement | null;
            const tag = target?.tagName?.toLowerCase();
            // Don't hijack typing inside inputs / textareas / contenteditable.
            if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) return;
            const search = document.querySelector<HTMLInputElement>('.invoice-table__search-field');
            if (search) {
                e.preventDefault();
                search.focus();
                search.select();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    return (
        <div className="dashboard-page">
            <div className="dashboard-layout">
                <Sidebar
                    activeItemId='all-invoices'
                    footer={null}
                    onClose={() => setIsSidebarOpen(false)}
                    className={`dashboard-sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}
                />
                {isSidebarOpen && (
                    <div
                        className="sidebar-overlay"
                        onClick={() => setIsSidebarOpen(false)}
                    />
                )}
                <div className="dashboard-main-content">
                    <Header setIsSidebarOpen={setIsSidebarOpen} />
                    <div className="manage-invoice-v2">
                        {/* Hero header — same soft violet card used by My Customers,
                            Drafts, My Freelancers, Deductions, Statements, Earnings,
                            Insurance, Pre-Insurance and Salary Calculator pages.
                            The Start tour button on the right (or below on mobile)
                            opens the in-page guided walkthrough. */}
                        <div className="mi-hero">
                            <span className="mi-hero__badge">
                                <HeroSparkles />
                                {t('INVOICE_CENTER', { defaultValue: 'Invoice Center' })}
                            </span>
                            <h1 className="mi-hero__title">
                                {renderHeroTitle(t('MANAGE_INVOICES'))}
                            </h1>
                            <p className="mi-hero__subtitle">
                                {t('MANAGE_INVOICES_SUBTITLE', { defaultValue: 'Track receivables, monitor payments, and keep your billing flowing.' })}
                            </p>
                            <button
                                type="button"
                                /* `is-pulsing` is toggled while the first-time
                                   prompt's skip-confirmation view is open —
                                   a soft violet halo ripples around this
                                   button so the user immediately sees *where*
                                   the tour lives if they want it later. Off
                                   again once the skip view is dismissed. */
                                className={`mi-hero__tour-btn${pulseStartTour ? ' is-pulsing' : ''}`}
                                onClick={() => setTourOpen(true)}
                                aria-label={t('TOUR_START', { defaultValue: 'Start tour' })}
                            >
                                <svg width={12} height={12} viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                    <path d="M5.83 14.95V5.05c0-1.38 1.59-2.17 2.68-1.32l6.44 4.95c.87.66.87 1.97 0 2.64l-6.44 4.95c-1.1.85-2.68.06-2.68-1.32z" fill="currentColor" />
                                </svg>
                                <span>{t('TOUR_START', { defaultValue: 'Start tour' })}</span>
                            </button>
                        </div>

                        {(summaryLoading || showInitialSkeleton) ? <KpiCardsSkeleton /> : (
                        <div className="manage-invoice-v2__kpis" data-tour="kpis">

                            
                            <div className="kpi-card">
                                <div className="kpi-card__top">
                                    <p className="kpi-card__label">{t('TOTAL_INVOICED', { defaultValue: 'Total Invoiced' })}</p>
                                    <span className="kpi-card__icon kpi-card__icon--total"><TrendingIcon /></span>
                                </div>
                                <p className="kpi-card__amount">{fmtAmount(totalAmount)} {currency}</p>
                                <div className="kpi-card__progress-row">
                                    <span>{t('COLLECTION_PROGRESS', { defaultValue: 'Collection progress' })}</span>
                                    <span>{collectionProgress.toFixed(1)}%</span>
                                </div>
                                <div className="kpi-card__progress-track">
                                    <div className="kpi-card__progress-fill" style={{ width: `${collectionProgress}%` }} />
                                </div>
                            </div>

                            <div className="kpi-card">
                                <div className="kpi-card__top">
                                    <p className="kpi-card__label">{t('PAID', { defaultValue: 'Paid' })}</p>
                                    <span className="kpi-card__icon kpi-card__icon--paid"><CheckCircleIcon /></span>
                                </div>
                                <p className="kpi-card__amount kpi-card__amount--paid">{fmtAmount(paidAmount)} {currency}</p>
                                <p className="kpi-card__hint">{t('CLEARED_AND_SETTLED', { defaultValue: 'Cleared and settled' })}</p>
                            </div>

                        </div>
                        )}

                        <div className="manage-invoice-v2__panel">
                            <div className="manage-invoice-v2__panel-head">
                                {/* `data-tour` wrappers are layout-neutral (just flex
                                    items) so they don't shift the existing panel-head
                                    layout; the tour uses them to locate spotlights. */}
                                <div data-tour="tabs">
                                    <PillTabs
                                        tabs={tabs}
                                        activeTab={activeTab}
                                        onTabChange={handleTabChange}
                                        ariaLabel={t('MANAGE_INVOICES')}
                                    />
                                </div>
                                <div data-tour="create">
                                    <DashboardButton
                                        onClick={handleInvoice}
                                        variant="primary"
                                        icon={<PlusIcon />}
                                    >
                                        {t('CREATE_INVOICE')}
                                    </DashboardButton>
                                </div>
                            </div>

                            <div className="manage-invoice-v2__tab-content" key={activeTab}>
                                {activeTab === 'manage' && (
                                    <InvoiceTableDetailed className="invoices-section" />
                                )}
                                {activeTab === 'credit' && (
                                    <React.Suspense fallback={<div className="invoice-table-container invoices-section"><InvoiceTableSkeleton columns={5} filters="search" /></div>}>
                                        <CreditInvoiceTableDetailed className="invoices-section" />
                                    </React.Suspense>
                                )}
                                {activeTab === 'shared' && (
                                    <React.Suspense fallback={<div className="invoice-table-container invoices-section"><InvoiceTableSkeleton columns={8} filters="full" /></div>}>
                                        <SharedInvoiceTableDetailed className="invoices-section" />
                                    </React.Suspense>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* First-time onboarding prompt — appears once on a user's
                very first visit and asks whether they want to take the
                guided tour.

                Visibility is SERVER-driven: the /useronboarding GET's
                `onBoardingSteps.IsManageInvoiceTourSkip` flag controls
                whether the modal mounts. Only shown while the flag is
                explicitly `false`; the moment the user picks either
                Skip for Now or Start Tour, we PUT the flag to `true`
                and refresh the GET, so the modal stays permanently
                hidden across every future visit (any device, any
                browser). This replaces the previous per-browser
                localStorage gate which lost state when the user
                cleared cookies or switched devices.

                Requires `userId` to be resolved so the auth context
                is ready when the PUT fires — otherwise the API call
                would run against an unauthenticated store slice. */}
            {userId && isManageInvoiceTourSkip === false && (
                <FirstTimeTourPrompt
                    /* storageKey is still passed because FirstTimeTourPrompt
                       internally uses it for its OWN visibility state,
                       BUT the parent-level `isManageInvoiceTourSkip === false`
                       guard is what actually decides mounting. Once the
                       server flag flips to true, the parent stops
                       rendering the component entirely. */
                    storageKey={`tour-prompt-seen:manage-invoice:user-${userId}`}
                    /* Show the animated Invozio tour-guide mascot (same
                       character as the Create-Invoice tour) instead of the
                       plain 👋 emoji. */
                    mascot
                    mascotBubble={t('TOUR_MI_MASCOT_BUBBLE', { defaultValue: "Let's go, explore Manage Invoice!" })}
                    /* Feature-specific ask-view copy — without these
                       overrides FirstTimeTourPrompt falls back to the
                       shared Timesheet keys (`TOUR_TS_*`) and users
                       would see "Welcome to Timesheet" here. Only the
                       title + body actually name the feature; the
                       Start / Skip button labels + the skip-view copy
                       are language-neutral and can inherit the
                       defaults. */
                    askTitle={t('TOUR_MI_FIRST_TIME_TITLE', { defaultValue: 'Welcome to Manage Invoice' })}
                    askBody={t('TOUR_MI_FIRST_TIME_BODY', { defaultValue: 'Is this your first time using Manage Invoice? Take a quick guided tour to learn how everything works.' })}
                    onStart={() => {
                        /* Fire the PUT + refresh in the background; do NOT
                           await, so the tour opens instantly with no
                           network latency between the user's click and
                           the first tour step. Even if the network fails
                           the tour still starts — the flag will simply
                           attempt to persist again on the next dismissal. */
                        updateUserOnboarding('isManageInvoiceTourSkip', true)
                            .then(() => dispatch(getUserOnboarding()))
                            .catch(() => { /* silent — non-blocking */ });
                        setTourOpen(true);
                    }}
                    /* Pulse starts when the user picks "Skip for Now"
                       (transition to the skip-confirmation view) and
                       continues until they click "Got it" on that
                       view. So the Start Tour CTA stays visibly
                       focused for the whole duration the user is
                       reading the "No problem!" message — no fixed
                       timer to outlive.

                       We ALSO fire the PUT here (not on Got it) so
                       the server flag flips immediately: a user who
                       closes the tab after clicking Skip for Now —
                       before ever reaching Got it — still won't see
                       the modal on their next visit. */
                    onSkip={() => {
                        updateUserOnboarding('isManageInvoiceTourSkip', true)
                            .then(() => dispatch(getUserOnboarding()))
                            .catch(() => { /* silent — non-blocking */ });
                        setPulseStartTour(true);
                    }}
                    onSkipDismiss={() => setPulseStartTour(false)}
                />
            )}

            {/* In-page guided tour. Portals to document.body so it sits
                above the violet panel's transform containing block. */}
            <ManageInvoiceTour open={tourOpen} onClose={() => setTourOpen(false)} userName={userName} />
        </div>
    );
}

export default ManageInvoicePage;
